import {
  MenuItem,
  Pagination,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { UsePaginationProps } from "@mui/material/usePagination/usePagination";
import { useCallback } from "react";
import { UsePaginationInstanceProps, UsePaginationState } from "react-table";

const availablePageLimitOptions = [20, 50, 100, 200] as const;

// export type TablePageLimitType = typeof availablePageLimitOptions[number];
export type TablePageLimitType = number;

interface Props<T extends object> {
  pageCount: UsePaginationInstanceProps<T>["pageCount"];
  pageIndex: UsePaginationState<T>["pageIndex"];
  pageSize: UsePaginationState<T>["pageSize"];
  setPageSize: UsePaginationInstanceProps<T>["setPageSize"];
  gotoPage: UsePaginationInstanceProps<T>["gotoPage"];
  /** Total number of results (from remote API). When set, shows "X–Y of Z". */
  totalResults?: number;
  /** When set (remote controlled), calling this updates parent instead of gotoPage */
  onRemotePageChange?: (page: number) => void;
  /** When set (remote controlled), call when page size changes so parent can sync */
  onRemotePageSizeChange?: (page: number, pageSize: number) => void;
  /** Show loading state (e.g. when fetching another page) */
  loading?: boolean;
}

const TablePagination = <T extends object>({
  pageCount,
  pageIndex,
  pageSize,
  setPageSize,
  gotoPage,
  totalResults,
  onRemotePageChange,
  onRemotePageSizeChange,
  loading = false,
}: Props<T>) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const handlePaginationChange = useCallback<
    Required<UsePaginationProps>["onChange"]
  >(
    (_event, page) => {
      if (onRemotePageChange) {
        onRemotePageChange(page);
      } else {
        gotoPage(page - 1);
      }
    },
    [gotoPage, onRemotePageChange],
  );

  const handlePageSizeChange = useCallback(
    (e: { target: { value: unknown } }) => {
      const newSize = Number(e.target.value);
      setPageSize(newSize);
      if (onRemotePageSizeChange) {
        onRemotePageSizeChange(pageIndex + 1, newSize);
      }
    },
    [setPageSize, onRemotePageSizeChange, pageIndex],
  );

  return (
    <Stack
      sx={{ p: { xs: 1, sm: 2 } }}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1}
    >
      {/* ~1/3: Page size */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          flex: { xs: "0 0 auto", sm: 1 },
          justifyContent: "flex-start",
          display: { xs: "none", sm: "flex" },
        }}
      >
        {loading ? (
          <>
            <Skeleton variant="text" width={64} height={24} />
            <Skeleton variant="rounded" width={56} height={32} />
          </>
        ) : (
          <>
            <Typography variant="body2">Page size</Typography>
            <Select
              variant="standard"
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              sx={{ minWidth: 56 }}
            >
              {availablePageLimitOptions.map((limit) => (
                <MenuItem key={limit} value={limit}>
                  {limit}
                </MenuItem>
              ))}
            </Select>
          </>
        )}
      </Stack>

      {/* ~1/3: Pagination */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{ flex: { xs: 1, sm: 1 } }}
      >
        {loading ? (
          <>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton
              variant="circular"
              width={32}
              height={32}
              sx={{ mx: 0.5 }}
            />
            <Skeleton variant="circular" width={32} height={32} />
          </>
        ) : (
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={handlePaginationChange}
            showFirstButton={isDesktop}
            showLastButton={isDesktop}
            boundaryCount={isDesktop ? 1 : 0}
            siblingCount={0}
            size={isDesktop ? "medium" : "small"}
          />
        )}
      </Stack>

      {/* ~1/3: Total results */}
      <Stack
        direction="row"
        alignItems="center"
        sx={{ flex: { xs: "0 0 auto", sm: 1 }, justifyContent: "flex-end" }}
      >
        {loading ? (
          <Skeleton variant="text" width={80} height={24} />
        ) : totalResults != null && totalResults > 0 ? (
          <Typography variant="body2" noWrap>
            {`${pageIndex * pageSize + 1}–${Math.min(
              (pageIndex + 1) * pageSize,
              totalResults,
            )} of ${totalResults.toLocaleString("en-US")}`}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default TablePagination;
